import React, { memo, useCallback, useEffect, useRef, useMemo, useState } from 'react';
import { UiBlocker } from '@bingads-webui-react/ui-blocker';
import { Body1Strong, mergeClasses } from '@fluentui/react-components';
import { HelpButton } from '@bingads-webui-react/help-popup';
import { ImageSuggestionCarousel } from '@bingads-webui-campaign-react/image-suggestion-carousel';
import { ImageRecommendationsPanel } from '@bingads-webui-campaign-react/pmax-image-experience';
import { RecommendationsSectionIcon } from './asset-images';
import { getCarouselStyles, getFullyShownImagesCount, initialImageContainerLeftMargin, useStyles } from './style';
import { DisplayRecommendedImageProps } from './type';
import { ImagePreview } from './ImagePreview';

const DisplayRecommendedImage = memo(({
  i18n,
  isLoading,
  type,
  showViewAllInHeader,
  seeAllLinkText,
  themes,
  onSelectRecommendedImage,
  recommendationTitle,
  title,
  seeAllPanelTitle,
  recommendedLoadingText,
  recommendedImages,
  maxUploadNum,
  uiBlocking,
  previewImage,
  helpTopic,
  helpLabel,
  isRecommendationEmpty = false,
  onEditPreviewImage,
  onReplacePreviewImage,
  hideEditPreviewBtn,
  hideReplacePreviewBtn,
  isMultiplePreview,
}: DisplayRecommendedImageProps) => {
  const [isSuggestionsPanelOpen, setIsSuggestionsPanelOpen] = useState(false);
  const [imagesToSuggest, setImagesToSuggest] = useState([]);
  const ref = useRef<{ fullUrl: any; index: any; suggestionId: any } | null>(null);
  const onSelectSeeAllRecommendedImages = useCallback(() => {
    setIsSuggestionsPanelOpen(true);
  }, []);
  const onDismiss = () => {
    setIsSuggestionsPanelOpen(false);
  };
  const onSelect = (fullUrl, index, suggestionId) => {
    onSelectRecommendedImage({ fullUrl, index, suggestionId }, type);
  };
  const onAdd = (images) => {
    onSelectRecommendedImage({ fullUrl: images[0]?.fullUrl, index: ref.current?.index, suggestionId: images[0]?.suggestionId }, type);
  };
  const onSelectImage = (fullUrl, index, suggestionId) => {
    ref.current = { fullUrl, index, suggestionId };
    onSelectRecommendedImage({ fullUrl, index, suggestionId }, type);
  };
  useEffect(() => {
    if (recommendedImages) {
      const truncatedImages = Array.isArray(recommendedImages) && recommendedImages.slice(0, 10);
      setImagesToSuggest(truncatedImages || [] as any);
    }
  }, [recommendedImages]);

  const classes = useStyles();

  const renderHelpBubble = useMemo(() => (
    <div className={classes.helpButton}>
      { helpTopic && (
        <HelpButton
          topic={helpTopic}
          aria-label={helpLabel}
        />
      )}
    </div>
  ), [classes.helpButton, helpLabel, helpTopic]);

  return (
    <div key={type} id={`display-action-pane-recommended-${type}`} className={mergeClasses(classes.displayRecommendedImage, 'display-action-pane-recommended')}>
      <div className={classes.titleContainer}>
        <Body1Strong>{title}</Body1Strong>
        {renderHelpBubble}
      </div>
      <ImagePreview i18n={i18n} isMultiplePreview={isMultiplePreview} type={type} onEdit={onEditPreviewImage} onReplace={onReplacePreviewImage} previewImage={previewImage} hideEditBtn={hideEditPreviewBtn} hideReplaceBtn={hideReplacePreviewBtn} />
      <UiBlocker
        blocking={uiBlocking}
        loadingText={recommendedLoadingText}
      >
        <ImageSuggestionCarousel
          title={recommendationTitle}
          isLoading={isLoading}
          showShimmer
          images={maxUploadNum > 0 ? imagesToSuggest : []}
          i18n={i18n}
          icon={RecommendationsSectionIcon}
          isEmpty={isRecommendationEmpty}
          onSelectImage={onSelect}
          onSelectSeeAll={onSelectSeeAllRecommendedImages}
          showViewAllInHeader={showViewAllInHeader}
          stylingOverrides={{
            getCarouselStyles,
            getFullyShownImagesCount,
            initialImageContainerLeftMargin,
            seeAllLinkText,
          }}
        />
      </UiBlocker>
      {isSuggestionsPanelOpen &&
        <ImageRecommendationsPanel
          i18n={i18n}
          isMultiSelect={false}
          title={seeAllPanelTitle}
          showThemes
          images={recommendedImages}
          themes={themes}
          hasMore={false}
          maxUploadNum={1}
          onAdd={onAdd}
          onSelectImage={onSelectImage}
          onDismiss={onDismiss}
        />
        }
    </div>
  );
});

export default DisplayRecommendedImage;
